// Utilities
import { defineStore } from 'pinia'
import { WatchHandle } from 'vue';
import i18n from '@/plugins/i18n'
import { doc, DocumentSnapshot, DocumentData, getDoc } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { logger } from '@/utils/logger'
import { Config } from '@/models/Config'
import { PublicConfig, type SupportChatConfig } from '@/models/PublicConfig'
import { TermsVersion } from '@/models/TermsVersion'

interface AppToastMessage {
  summary: string
  detail: string
  severity: 'info' | 'success' | 'warn' | 'error'
  life: number
}


export const useAppStore = defineStore('app', {
  state: () => ({
    debug: false,
    _initHandle: null as WatchHandle | null,
    pendingToasts: [] as AppToastMessage[],
    currentTitle: null as string | null,
    configSnapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    configLoaded: false,
    publicConfigSnapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    publicConfigLoaded: false,
    // In-force terms-of-use version (MYAC-824), loaded from terms_versions/{v}
    // where v = public_config.terms.current_version. Cached per version so the
    // text shown and the version consent is recorded against are one doc.
    inForceTermsSnapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    inForceTermsForVersion: null as string | null,
  }),
  getters: {
    config(): Config | null {
      if (!this.configSnapshot || !this.configSnapshot.exists()) return null
      return Config.fromFirebase(this.configSnapshot)
    },
    chatbotUrl(): string | null {
      return this.config?.chatbot_url || null
    },
    chatbotName(): string | null {
      return this.config?.chatbot_name || null
    },
    chatbotResponseFormat(): 'markdown' | 'json_markdown' | null {
      return this.config?.chatbot_response_format || null
    },
    /** Base URL of the released agents service (change control specialist, …). */
    agentsUrl(): string | null {
      return this.config?.agents_url || null
    },
    publicConfig(): PublicConfig | null {
      if (!this.publicConfigSnapshot || !this.publicConfigSnapshot.exists()) return null
      return PublicConfig.fromFirebase(this.publicConfigSnapshot)
    },
    /** MYAC-1348 — Plain support chat settings for this environment, or null when off. */
    supportChatConfig(): SupportChatConfig | null {
      return this.publicConfig?.support_chat || null
    },
    // self-serve gate moved to featureFlagsStore.isSelfServeEnabled (MYAC-837) so
    // it can carry per-tenant overrides; publicConfig.self_serve_enabled is the
    // global input to that resolver.
    /** URL used by the Contact-sales modal CTA (falls back handled in the modal). */
    contactSalesUrl(): string | null {
      return this.publicConfig?.contact_sales_url || null
    },
    /** The in-force terms-of-use version (body + metadata), or null if none. */
    inForceTerms(): TermsVersion | null {
      if (!this.inForceTermsSnapshot || !this.inForceTermsSnapshot.exists()) return null
      return TermsVersion.fromFirebase(this.inForceTermsSnapshot)
    },
  },
  actions: {
    setDebug: function (debug: boolean) {
      this.debug = debug;
    },
    toast: function (summary: string, detail: string, severity: 'info' | 'success' | 'warn' | 'error', life: number = 5000) {
      this.pendingToasts.push({ summary, detail, severity, life })
    },
    setTitle: function (titleKey: string | null) {
      this.currentTitle = titleKey
      const brandName = i18n.global.t('pageTitles.brandName')
      let title: string
      if (titleKey === null) {
        // Use default title (just brand name)
        title = brandName
      } else {
        // Translate the key
        title = i18n.global.t(titleKey)
        // If translation fails (returns the key), fall back to default
        if (title === titleKey) {
          title = brandName
        } else {
          // Prepend brand name to page titles
          title = `${brandName} - ${title}`
        }
      }
      document.title = title
    },
    loadConfig: async function () {
      // Only load once - check is inside the action
      if (this.configLoaded) {
        return
      }

      try {
        const configDoc = doc(useFirestore(), 'system', 'config')
        const snapshot = await getDoc(configDoc)
        this.configSnapshot = snapshot
        this.configLoaded = true
        logger.debug('System config loaded', snapshot.exists() ? snapshot.data() : 'document does not exist')
      } catch (error) {
        // Handle document not existing or permission errors gracefully
        logger.info('System config document not found or inaccessible', error)
        this.configSnapshot = null
        this.configLoaded = true // Mark as loaded even on error to prevent retries
      }
    },
    loadPublicConfig: async function () {
      // Only load once - check is inside the action
      if (this.publicConfigLoaded) {
        return
      }

      try {
        const publicConfigDoc = doc(useFirestore(), 'system', 'public_config')
        const snapshot = await getDoc(publicConfigDoc)
        this.publicConfigSnapshot = snapshot
        this.publicConfigLoaded = true
        logger.debug('Public config loaded', snapshot.exists() ? snapshot.data() : 'document does not exist')
      } catch (error) {
        // Handle document not existing or permission errors gracefully
        logger.info('Public config document not found or inaccessible', error)
        this.publicConfigSnapshot = null
        this.publicConfigLoaded = true // Mark as loaded even on error to prevent retries
      }
    },
    /**
     * Load the in-force terms-of-use version (MYAC-824). Reads the pointer in
     * public_config.terms to learn which version is current, then fetches that
     * terms_versions/{version} doc (body + metadata). Cached per version. The
     * body rendered on the register/re-consent screens and the version+hash the
     * consent is recorded against therefore come from this same doc — no drift.
     * Returns null when no terms are in force (feature not activated in this env).
     */
    loadInForceTerms: async function (): Promise<TermsVersion | null> {
      await this.loadPublicConfig()
      const version = this.publicConfig?.terms?.current_version
      if (!version) {
        this.inForceTermsSnapshot = null
        this.inForceTermsForVersion = null
        return null
      }
      if (this.inForceTermsForVersion === version && this.inForceTermsSnapshot) {
        return this.inForceTerms
      }
      try {
        const termsDoc = doc(useFirestore(), 'terms_versions', version)
        this.inForceTermsSnapshot = await getDoc(termsDoc)
        this.inForceTermsForVersion = version
      } catch (error) {
        // Public-read; a transient failure must not wedge registration/re-consent.
        logger.info('In-force terms document not found or inaccessible', error)
        this.inForceTermsSnapshot = null
        this.inForceTermsForVersion = null
      }
      return this.inForceTerms
    },
  }
})
